import {
  type TeamExternalIntegration,
  type StatusCheckEvaluationPredicate,
  type ResponseBodyEvaluation,
  ObservabilityToolType
} from 'types/api/status-checks/status-check'
import { OBSERVABILITY_TOOL_DISPLAY_NAME } from 'utils/status-checks/status-checks-utils'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'

export const grafanaStatusCheckIntegration: TeamExternalIntegration = {
  name: OBSERVABILITY_TOOL_DISPLAY_NAME[ObservabilityToolType.GRAFANA],
  type: IntegrationType.Grafana,
  authUrl: '',
  isPrivateNetworkIntegration: false,
  headers: [{ key: 'Authorization', value: '', label: 'API Key' }],
  url: 'https://<your_domain>.grafana.net/',
  sortIndex: 3
}

export const grafanaBaseUrlRegex = new RegExp(
  '^https:\\/\\/(.+).grafana.net\\/?$'
)

export const grafanaRegex = new RegExp(
  '^https:\\/\\/(.+).grafana.net\\/alerting\\/grafana\\/([a-zA-Z0-9]+)(/view)?$'
)

export const grafanaApiAlertsRegex = new RegExp(
  '^https:\\/\\/(.+).grafana.net\\/api\\/alertmanager\\/grafana\\/api\\/v2\\/alerts\\?alertId=(.+)$'
)

export const grafanaResponseBodyEvaluationConfiguration: ResponseBodyEvaluation =
  {
    op: 'AND',
    predicates: []
  }

export const GRAFANA_MONITOR_URL_PREFIX =
  'api/alertmanager/grafana/api/v2/alerts?alertId='

export function convertToGrafanaUrl(url: string): string {
  if (grafanaRegex.test(url)) {
    const domainName = grafanaRegex.exec(url)?.[1]
    const alertId = grafanaRegex.exec(url)?.[2]
    if (domainName) {
      return `https://${domainName}.grafana.net/${GRAFANA_MONITOR_URL_PREFIX}${alertId}`
    }
  }

  if (grafanaBaseUrlRegex.test(url)) {
    const domainName = grafanaBaseUrlRegex.exec(url)?.[1]
    return `https://${domainName}.grafana.net/api/alertmanager/grafana/api/v2/alerts`
  }

  if (grafanaApiAlertsRegex.test(url)) {
    return url
  }
  return null
}

function getDomainNameFromUrl(url: string): string {
  if (grafanaRegex.test(url)) {
    return grafanaRegex.exec(url)?.[1]
  }

  if (grafanaBaseUrlRegex.test(url)) {
    return grafanaBaseUrlRegex.exec(url)?.[1]
  }

  if (grafanaApiAlertsRegex.test(url)) {
    return grafanaApiAlertsRegex.exec(url)?.[1]
  }

  return null
}

export function convertToGrafanaBaseUrl(url: string): string {
  const domainName = getDomainNameFromUrl(url)

  if (domainName) {
    return `https://${domainName}.grafana.net/`
  }

  return null
}

export function getAlertIdFromUrl(url: string) {
  if (grafanaRegex.test(url)) {
    return grafanaRegex.exec(url)?.[2]
  }

  if (grafanaApiAlertsRegex.test(url)) {
    return grafanaApiAlertsRegex.exec(url)?.[2]
  }

  const alertId = new URL(url).searchParams.get('alertId')

  if (!alertId) {
    throw new Error(
      'We were unable to extract the Alert ID from the provided URL. Please ensure the URL contains a valid alert identifier.'
    )
  }

  return alertId
}
/*
The BE makes a call to Grafana to get back a list of all firing alerts,
we are using NEQ as the comparator to see if the url entered is not one of the firing alerts.
 */
export const formatResponseBodyEvaluationPredicatesGrafana = (
  url: string
): ResponseBodyEvaluation => {
  return {
    op: 'AND',
    predicates: [
      {
        type: 'String',
        jpQuery: `[?(@.labels.__alert_rule_uid__ == '${getAlertIdFromUrl(
          url
        )}')].labels.__alert_rule_uid__`,
        rValue: getAlertIdFromUrl(url),
        comparator: 'NEQ'
      } as StatusCheckEvaluationPredicate
    ]
  }
}
